/**
 * fiscal-api — klient tras fiskalizacji.
 *
 * PUŁAPKA, KTÓRA JUŻ RAZ KOSZTOWAŁA GODZINĘ: `api` ma ustawione `baseURL: '/api'`, więc ścieżki
 * podajemy BEZ tego prefiksu. Ścieżka zaczynająca się od `/api/` daje `/api/api/...`, czyli 404,
 * a ekran chowa sekcję zamiast pokazać błąd — pilnuje tego test.
 */

import { api } from '@/lib/api';

export type FiscalPrintStatus =
  | 'pending'
  | 'claimed'
  | 'sent'
  | 'printed'
  | 'failed'
  | 'unknown'
  | 'cancelled';

export interface FiscalPrintJobState {
  id: string;
  status: FiscalPrintStatus;
  docKind: string;
  stationId: string;
  receiptNumber: string | null;
  fiscalDeviceNo: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  symfoniaMarkedAt: string | null;
  symfoniaLastError: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface FiscalStationListItem {
  id: string;
  name: string;
  code: string;
  printerVendor: 'posnet' | 'elzab' | 'novitus';
  printerModel: string | null;
  connectionKind: 'serial' | 'ethernet';
  connectionConfig: Record<string, unknown>;
  fiscalDeviceNo: string | null;
  vatLetterMap: Array<{ vatRateId: number; vatRateCode?: string | null; letter: string }>;
  paymentTypeMap: Array<{ paymentFormId: number; paymentFormName?: string | null; type: string }>;
  maxItemNameLength: number;
  allowProductionPrinting: boolean;
  isActive: boolean;
  tokenSet: boolean;
  tokenPrefix: string | null;
  agentVersion: string | null;
  lastSeenAt: string | null;
  online: boolean;
  printerStatus: Record<string, unknown> | null;
}

/** Powód odmowy druku — z kodem, numerem pozycji i gotowym komunikatem po polsku. */
export interface FiscalMappingErrorDto {
  code: string;
  positionNo: number | null;
  message: string;
}

export async function listStations(companyId: string): Promise<FiscalStationListItem[]> {
  const res = await api.get<{ items: FiscalStationListItem[] }>(
    `/fiscal/stations?companyId=${companyId}`,
  );
  return res.data.items;
}

export async function queueReceiptPrint(input: {
  documentId: number | string;
  companyId: string;
  stationId: string;
  includeBuyerTaxId: boolean;
}): Promise<{ jobId: string }> {
  const res = await api.post<{ jobId: string }>(
    `/handel-magazyn/sales/${input.documentId}/print-fiscal?companyId=${input.companyId}`,
    { stationId: input.stationId, includeBuyerTaxId: input.includeBuyerTaxId },
  );
  return res.data;
}

export async function getPrintJob(jobId: string, companyId: string): Promise<FiscalPrintJobState> {
  const res = await api.get<FiscalPrintJobState>(
    `/fiscal/print-jobs/${jobId}?companyId=${companyId}`,
  );
  return res.data;
}

export async function resolvePrintJob(
  jobId: string,
  companyId: string,
  decision:
    | { outcome: 'printed'; receiptNumber: string; fiscalDeviceNo: string; note?: string }
    | { outcome: 'failed'; note?: string },
): Promise<void> {
  await api.post(`/fiscal/print-jobs/${jobId}/resolve?companyId=${companyId}`, decision);
}

/** Stany, w których warto dalej odpytywać o postęp. */
export function isPrintJobInProgress(status: FiscalPrintStatus): boolean {
  return status === 'pending' || status === 'claimed' || status === 'sent';
}

/** Urządzenie przypisane do zalogowanego użytkownika (podpowiedź przy druku). */
export interface AssignedStation {
  stationId: string;
  name: string;
  isActive: boolean;
}

export async function getMyStation(companyId: string): Promise<AssignedStation | null> {
  const res = await api.get<{ assigned: AssignedStation | null }>(
    `/fiscal/my-station?companyId=${companyId}`,
  );
  return res.data.assigned;
}

export async function setMyStation(companyId: string, stationId: string | null): Promise<void> {
  await api.put(`/fiscal/my-station?companyId=${companyId}`, { stationId });
}

/** Słowniki Symfonii potrzebne do skonfigurowania map drukarki. */
export interface FiscalSymfoniaDictionaries {
  vatRates: Array<{ id: number; code: string; description: string | null }>;
  paymentForms: Array<{ id: number; name: string }>;
}

export async function getSymfoniaDictionaries(
  companyId: string,
): Promise<FiscalSymfoniaDictionaries> {
  const res = await api.get<FiscalSymfoniaDictionaries>(
    `/fiscal/symfonia-dictionaries?companyId=${companyId}`,
  );
  return res.data;
}

/** Instalator usługi fiskalnej — jedno NAJNOWSZE opublikowane wydanie per system. */
export interface FiscalAgentRelease {
  id: string;
  platform: 'windows' | 'macos';
  version: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  notes: string | null;
  publishedAt: string | null;
}

export async function listAgentReleases(companyId: string): Promise<FiscalAgentRelease[]> {
  const res = await api.get<{ items: FiscalAgentRelease[] }>(
    `/fiscal/agent-releases?companyId=${companyId}`,
  );
  return res.data.items;
}

/**
 * Pobranie instalatora idzie przez NASZĄ trasę (autoryzacja), więc nie da się go wystawić
 * zwykłym odsyłaczem — plik ściągamy do pamięci i oddajemy przeglądarce jako zapis pliku.
 */
export async function downloadAgentRelease(
  release: FiscalAgentRelease,
  companyId: string,
): Promise<void> {
  const res = await api.get<Blob>(
    `/fiscal/agent-releases/${release.id}/download?companyId=${companyId}`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(res.data);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = release.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Bez zwolnienia adresu plik zostaje w pamięci karty do jej zamknięcia — przy instalatorze
    // idącym w setki megabajtów to realny koszt, nie formalność.
    URL.revokeObjectURL(url);
  }
}
